import { useAuthentication } from '@ravago/shared/authentication';
import { CountryUtils, CustomerUtils, ProductUtils, getSystemPageUrl } from '@ravago/shared/next-data-access/client';
import { SystemPageType } from '@ravago/shared/page-data/enums/system-page-type';
import { ProductDetail } from '@ravago/shared/page-data/models/elements';

import { useQuery } from '@tanstack/react-query';

import { usePathLanguageCode } from '../../../../hooks/use-path-locale';

interface Props {
   productId: string;
   defaultProduct?: ProductDetail;
   isCustomerProduct: boolean;
}

export const useRequestForQuotationFlyoutState = ({ productId, defaultProduct, isCustomerProduct }: Props) => {
   const { accessToken } = useAuthentication();
   const language = usePathLanguageCode();

   const productDetailQuery = useQuery({
      queryKey: ['product-details', productId],
      queryFn: () => ProductUtils.getProductDetails(productId),
      initialData: defaultProduct,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: !isCustomerProduct
   });

   const colorsQuery = useQuery({
      queryKey: ['customer-products', productId, 'colors'],
      queryFn: () => ProductUtils.getCustomerProductColors(productId),
      enabled: isCustomerProduct
   });

   const packagingGroupsQuery = useQuery({
      queryKey: ['customer-products', productId, 'packaging-groups'],
      queryFn: () => ProductUtils.getCustomerProductPackagingGroups(productId),
      enabled: isCustomerProduct
   });

   const customerQuery = useQuery({
      queryKey: ['current-customer'],
      queryFn: () => CustomerUtils.getCurrentCustomer(),
      enabled: !!accessToken
   });

   const countriesQuery = useQuery({
      queryKey: ['country-locales'],
      queryFn: () => CountryUtils.getCountries()
   });

   const myProductsAndPricesRoute = useQuery({
      queryKey: ['system-page-route', SystemPageType.MY_PRODUCTS_AND_PRICES],
      queryFn: () => getSystemPageUrl(language, SystemPageType.MY_PRODUCTS_AND_PRICES)
   });

   const product = defaultProduct || productDetailQuery.data;
   const colors = isCustomerProduct ? colorsQuery.data : product?.colors;
   const packagingGroups = isCustomerProduct ? packagingGroupsQuery.data : product?.packagingGroups;

   const loading =
      (isCustomerProduct ? colorsQuery.isLoading || packagingGroupsQuery.isLoading : productDetailQuery.isLoading) ||
      customerQuery.isLoading ||
      countriesQuery.isLoading;

   return {
      colors,
      packagingGroups,
      customer: customerQuery.data,
      countries: countriesQuery.data,
      myProductsAndPricesRoute: myProductsAndPricesRoute.data,
      loading
   };
};
